import { UiControlConstants } from "../../common/uicontrol-constants";
import { DocumentInteractionService } from "../../interaction/documentInteractionSvc";
import { LocalizationService } from "../../localization/localization";
import { ContentPageAction, TelemetryService, CustomKeyValues } from "../../logger/telemetry-service";
import { GlobalTelemetryConstants } from "../../logger/telemetry-constants";
import { FilterableColumn, primitiveFilterer, refreshFiltersEventName } from "./table-filter-select";
import { PageableDataSource } from "./table-paging";
import { PaginationFunctionality, SdkGridOptions, TableFunctionality } from "./table-paging-functionality";
import { ColumnController } from "./tableColumn";
import { v4 } from "uuid";
import { GlobalTableConstants } from "./table-constants";

/** ###TypeDocName:sd-table### */
export class TableBinding {
  /**
   * The array of objects that will represent your table data. Where each row in the table corresponds to each object in the array.
   * Each field in the objects can be assigned to a column.  Should not be used in conjunction with the dataSource property.
   */
  public data = "<";

  /**
   * The instance of PageableDataSource that should be used for paged data lookup. Should not be used in conjunction with the data property.
   */
  public pageableDataSource = "<";

  /** true/false Whether the table is sortable by clicking on the column header.*/
  public sortable = "@?";

  /** true/false Whether the columns are resizable by dragging the area between the columns. */
  public resizableColumns = "@?";

  /** true/false Whether an 'export to CSV' button will appear below the table. */
  public csvExport = "@?";

  /** true/false Whether the user can rearrange column order by clicking and dragging. Also enables pinning. */
  public arrangeableColumns = "@?";

  /** number in pixels for the default row height. Defaults to 35.*/
  public rowHeight = "@?";

  /** number in pixels for the default header height. Defaults to 25. */
  public headerHeight = "@?";

  /** number in pixels for the row height once a row is expanded. Defaults to 200.*/
  public expandedRowHeight = "@?";

  /** true/false if the expander column should be hidden, if hidden, expandable rows default to shown. Defaults to 'false' */
  public expanderColumnHidden = "@?";

  /** true/false Whether the table is searchable via associated search box.*/
  public searchable = "<?";

  /** fn called when search is performed on the grid. Parameter '{ searchText: string, rowItem: any, columnFieldDef: any }' is passed to the function.
   * Return true for rows to be displayed or false to hide the given row.
   */
  public onSearch = "&?";

  /** placeholder text for search in table.*/
  public searchPlaceholder = "@?";

  /** Turn on row selection, and set it to Single or Multiple selection. See: ServiceDesk.Controls.Table.RowSelectMode*/
  public rowSelectMode = "@?";

  /**
   * Used to enable delta row data mode in ag-grid. Must conform to the Table.DeltaRowDataModeOptions interface. This is primarily used for
   * enabling the feature of ag-grid which 'automatically' links up old data to new data when replacing the contents of the data array.
   * If you see flickering when updating content, look into using this feature (documented in ag-grid here: "Bulk Method 3 - Delta Row Data" --
   * https://www.ag-grid.com/javascript-grid-data-update/ ).
   */
  public deltaRowConfiguration = "<";

  /**
   * DEPRECATED
   * Determines whether the scope stays alive if the user scrolls out of view of an expanded row. Defaults to false.
   * Setting this to true may cause performance issues if many rows are expanded.
   */
  public keepExpandedScopeOnScroll = "@?";

  /**
   * DEPRECATED
   * Determines whether the scope stays alive for an expanded row if the row is collapsed. Defaults to false.
   * Setting this to true may cause performance issues if many rows are expanded.
   */
  public keepExpandedScopeOnCollapse = "@?";

  /** The area identifier for all business intelligence (BI) actions triggered by this component.*/
  public biArea = "@?";
  /**
   * Allow table to set height based on number of rows. Removes virtualization of rows and SHOULD NOT be used on large data sets.
   */
  public autoHeight = "@?";

  /**
   * Table will render cells faster and scroll faster at the expense of possibly not running interpolation (the angular {{}} notation). Use ng-bind to remedy.
   */
  public fastRender = "<?";
  /**
   * true/false Table will resize columms to fit to available width.
   */
  public autoFitColumns = "<?";

  /**
   * custome data logging in type "any".
   */
  public telemetryData = "<";
  /**
   * for scenario based telemetry, log scenario and scenario step in pageMetaTags.
   */
  public pageMetaTags = "<";
}

export interface DeltaRowDataModeOptions {
  getRowNodeId<T>(data: T): any;
}

export interface SdTableSortModel {
  sort: any;
  columnId: string;
}

export interface SdTableColumnOrder {
  toIndex: number;
  columnId: string;
  columns: string[];
}

export interface SdTableColumnSize {
  columnId: string;
  width: number;
}

export interface SdTableMethods {
  // callbacks
  onColumnMoved?: (columnOrder: SdTableColumnOrder) => void;
  onColumnResized?: (columnSize: SdTableColumnSize) => void;
  onColumnSorted?: (sortModel: SdTableSortModel[]) => void;
  onRowLifecycleEvent?: (changeType: any, rowData: any) => void;
  onRowSelectionChanged?: (selectedRowData: any[]) => void;
  onRowClicked?: (rowData: any) => void;

  // table modifier methods
  moveColumnToIndex: (columnId: string, toIndex: number) => void;
  refreshFilters: () => void;
  refreshTable: () => void;
  setColumnSort: (columnId: string, sort: any) => void;
  setColumnWidth: (columnId: string, width: number) => void;
  setRowExpansionState: (rowData: any, isExpanded: boolean) => void;
  setRowSelection: (rowDataToSelect: any[]) => void;
  exportTableToCsv: (exportOptions?: { fileName: string }) => void;

  // save state properties
  columnOrder?: string[];
  columnSizes?: { [columnId: string]: SdTableColumnSize };
  rowSelection?: any[];
  sortModel?: SdTableSortModel[];
  viewModel?: any;
}

export type RowSelectMode = "Single" | "Multiple";

const ascendingString = "asc";
const descendingString = "desc";
const expanderColumnId = "sdTableInternalColumnRowId";
const cellContainerClass = "sd-cell-container";
const cellDefaultClass = "sd-table-cell";
const rightAlignmentClass = "sd-table-right-aligned";
const centerAlignmentClass = "sd-table-center-aligned";
const leftAlignmentClass = "sd-table-left-aligned";
const verticalTopAlignmentClass = "sd-table-cell-top-aligned";
const verticalCenterAlignmentClass = "sd-table-cell-center-aligned";
const verticalBottomAlignmentClass = "sd-table-cell-bottom-aligned";
const defaultRowHeight = 35;
const defaultExpandedRowHeight = 200;

export class TableController {
  public columnDefinitions: any[] = [];
  public columnFieldDefinitions: any[] = [];
  public data: any;
  public resizableColumns: boolean;
  public arrangeableColumns: boolean;
  public sortable: boolean;
  public rowHeight: string;
  public headerHeight: number;
  public expandedRowHeight: string;
  public gridOptions: any;
  public csvExport: boolean;
  public allowExportAsCsv: boolean;
  public compileTransclusionFn: ng.ITranscludeFunction;
  public compileScope: any;
  public keepExpandedScopeOnCollapse: string;
  public keepExpandedScopeOnScroll: string;
  public isReadyToProcessDataUpdates = false;
  public searchable: boolean;
  public searchText: string;
  public onSearch: (payload: { searchText: string; rowItem: any; columnFieldDef: any }) => boolean;
  public searchPlaceholder: string;
  public id: string;
  public biArea: string;
  public agBodyViewport: HTMLElement;
  public agBodyContainer: HTMLElement;
  public deltaRowConfiguration: DeltaRowDataModeOptions;
  public autoHeight: boolean;
  public pageableDataSource: PageableDataSource;
  public fastRender: boolean;
  public autoFitColumns: boolean;
  public telemetryData: CustomKeyValues;
  public pageMetaTags: CustomKeyValues;
  private firstLoad = true;
  private readyForAgGridToStartRendering = false;

  private expanderColumnHidden: boolean;
  private agGridRowIsExpanding = false;
  private requiresModelUpdate = false;
  private lastColumnFocused: string;
  private aliveCellScopes = 0;
  private isExpandable = false;
  private aliveRowScopes = 0;
  private filterableColumns: FilterableColumn[] = [];
  private rowSelectMode: RowSelectMode;
  private shiftKeyDown = false;
  private parsedRowHeight: number;
  private parsedExpandedRowHeight: number;
  private oldData: any;

  private headerColumnLabel: string;
  private headerNonSortableLabel: string;
  private headerSortableLabel: string;
  private headerAcsendingLabel: string;
  private headerDescendingLabel: string;
  private expandableRowButtonLabelPrefix: string;
  private expandableRowButtonLabelPostfix: string;
  private expandableRowButtonExpandedLabel: string;
  private expandableRowButtonContractedLabel: string;
  private expandableRowLabelPrefix: string;
  private expandableRowLabelPostfix: string;
  private cellRowLabelPrefix: string;
  private cellColumnLabelPrefix: string;
  private cellHeaderValueLabelSpacer: string;
  private pendingTableData: any[];
  public functionalities = {
    pagination: <PaginationFunctionality>undefined
  };
  // @ngInject
  constructor(
    private $scope: ng.IScope,
    private $compile: ng.ICompileService,
    private $rootScope: ng.IRootScopeService,
    private $timeout: ng.ITimeoutService,
    private sdLocalizationService: LocalizationService,
    private documentInteractionService: DocumentInteractionService,
    private telemetryService: TelemetryService,
    private $element: ng.IRootElementService,
    private telemetryConstants: GlobalTelemetryConstants,
    private tableConstants: GlobalTableConstants,
    private uiControlConstants: UiControlConstants
  ) {
    this.id = v4();
    this.parsedRowHeight = defaultRowHeight;
    this.parsedExpandedRowHeight = defaultExpandedRowHeight;
    this.functionalities.pagination = new PaginationFunctionality(this, $scope, this.sdLocalizationService);
  }

  private keyEvent(event: any) {
    this.shiftKeyDown = event.shiftKey;
  }

  private addViewModelToData() {
    if (this.data || this.functionalities.pagination.isEnabled) {
      let tableViewModel: SdTableMethods = {
        refreshTable: this.updateData.bind(this),
        refreshFilters: this.refreshFilters.bind(this),
        setRowExpansionState: this.setRowExpansionState.bind(this),
        setColumnSort: this.setColumnSort.bind(this),
        moveColumnToIndex: this.moveColumn.bind(this),
        setColumnWidth: this.setColumnWidth.bind(this),
        setRowSelection: this.setRowSelection.bind(this),
        exportTableToCsv: this.exportAsCsv.bind(this)
      };
      let internalViewModel = this.getInternalViewModel();
      angular.merge(internalViewModel, tableViewModel);
      // For backwards compatibility, add tableViewModel to vm.viewModel.
      if (internalViewModel.viewModel) {
        angular.merge(internalViewModel.viewModel, tableViewModel);
      } else {
        internalViewModel.viewModel = tableViewModel;
      }
    }
  }

  private scrollElementWithTable(element: HTMLElement) {
    if (element.parentElement) {
      let scrollLeft = this.agBodyViewport.scrollLeft;
      element.style.width = this.agBodyContainer.style.width;
      element.parentElement.scrollLeft = scrollLeft;
    }
  }

  private onBodyScroll(params: any, params2: any) {
    if (params.direction == "horizontal") {
      let renderedExpandedNodes = this.gridOptions.api.getRenderedNodes();
      renderedExpandedNodes.forEach((row: any) => {
        if (row.expanded && row._rowElement) {
          this.scrollElementWithTable(row._rowElement.parentElement);
        }
      });
    }

    if (this.functionalities.pagination.isEnabled) {
      this.functionalities.pagination.onBodyScroll(params, params2);
    }
  }

  public updateData() {
    this.notifyRowsOfLifeCycleEvent(this.oldData, this.tableConstants.rowLifecycleEventType.DATA_CHANGED);
    this.initializeTableState();
    this.gridOptions.api.refreshView();
  }

  private onColumnMoved(params: any) {
    let internalViewModel = this.getInternalViewModel();
    if (params.column) {
      internalViewModel.columnOrder = this.gridOptions.columnApi.getAllGridColumns().map((column: any) => {
        return column.colId;
      });
      if (angular.isFunction(internalViewModel.onColumnMoved)) {
        internalViewModel.onColumnMoved({
          toIndex: params.toIndex,
          columnId: params.column.colId,
          columns: internalViewModel.columnOrder
        });
      }

      let colToRight =
        params.toIndex + 1 < this.columnDefinitions.length ? this.columnDefinitions[params.toIndex + 1].id : "None";
      let colToLeft = params.toIndex > 0 ? this.columnDefinitions[params.toIndex].id : "None";
      this.invokeTelemetry(
        `Column ${params.column.colDef.id} Moved`,
        `Moved Position: ${params.toIndex}. Column to the right:${colToRight} Columns to the left:${colToLeft}`,
        this.telemetryConstants.pageActionType.click
      );
    }
  }

  private onColumnSorted() {
    let sortModel = this.gridOptions.api.getSortModel();
    let returnModel: SdTableSortModel[] = [];
    if (sortModel.length > 0) {
      returnModel.push({ columnId: sortModel[0].colId, sort: this.convertSortStringToEnum(sortModel[0].sort) });
      if (this.pageMetaTags) {
        this.pageMetaTags[this.telemetryConstants.pageMetadataTags.scenarioStep] =
          this.pageMetaTags[this.telemetryConstants.pageMetadataTags.scenario] + ".SortColumn";
      }
      this.invokeTelemetry(
        `Column ${sortModel[0].colId} Sorted`,
        this.tableConstants.columnSortMethod[this.convertSortStringToEnum(sortModel[0].sort)],
        this.telemetryConstants.pageActionType.click
      );
    }
    let internalViewModel = this.getInternalViewModel();
    internalViewModel.sortModel = returnModel;

    if (this.functionalities.pagination.isEnabled) {
      this.functionalities.pagination.onSortChanged();
    }

    if (angular.isFunction(internalViewModel.onColumnSorted)) {
      internalViewModel.onColumnSorted(returnModel);
    }
  }

  private onRowSelectionChanged() {
    let internalViewModel = this.getInternalViewModel();
    internalViewModel.rowSelection = this.gridOptions.api.getSelectedRows();
    if (angular.isFunction(internalViewModel.onRowSelectionChanged)) {
      internalViewModel.onRowSelectionChanged(internalViewModel.rowSelection);
    }
  }

  public onRowClicked(params: any) {
    // Ag-grid like selection behavior with the exception of how we process clicks on selected rows.
    let multiSelectKeyPressed = params.ctrlKey || params.metaKey;
    let shiftKeyPressed = params.shiftKey;
    if (params.node.group || params.node.floating) {
      return;
    }

    if (this.gridOptions.rowSelection) {
      let wasSelected: boolean;
      if (params.node.isSelected()) {
        params.node.setSelectedParams({ newValue: false });
      } else if (multiSelectKeyPressed) {
        params.node.setSelectedParams({
          newValue: true,
          clearSelection: !multiSelectKeyPressed,
          rangeSelect: shiftKeyPressed
        });
        wasSelected = true;
      } else {
        // selected with no multi key, must make sure anything else is unselected
        params.node.setSelectedParams({ newValue: true, clearSelection: true });
        wasSelected = true;
      }

      this.invokeTelemetry(
        "Row Selected",
        `Row ${params.rowIndex} was${wasSelected ? " not" : ""} selected`,
        this.telemetryConstants.pageActionType.click
      );
    }

    let internalViewModel = this.getInternalViewModel();
    if (!params.node.flower && angular.isFunction(internalViewModel.onRowClicked)) {
      internalViewModel.onRowClicked(params.node.data);
    }
  }

  private convertSortStringToEnum(sort: string) {
    switch (sort) {
      case ascendingString:
        return this.tableConstants.columnSortMethod.ASCENDING;
      case descendingString:
        return this.tableConstants.columnSortMethod.DESCENDING;
      default:
        return this.tableConstants.columnSortMethod.UNSORTED;
    }
  }

  private convertSortEmumToSortString(sort: any) {
    switch (sort) {
      case this.tableConstants.columnSortMethod.ASCENDING:
        return ascendingString;
      case this.tableConstants.columnSortMethod.DESCENDING:
        return descendingString;
      case this.tableConstants.columnSortMethod.UNSORTED:
      default:
        return undefined;
    }
  }

  private onColumnResized(params: any) {
    let internalViewModel = this.getInternalViewModel();
    if (params.finished) {
      if (!internalViewModel.columnSizes) {
        internalViewModel.columnSizes = {};
      }
      let columnSize = { columnId: params.column.colId, width: params.column.actualWidth };
      internalViewModel.columnSizes[params.column.colId] = columnSize;
      if (angular.isFunction(internalViewModel.onColumnResized)) {
        internalViewModel.onColumnResized(columnSize);
      }
    }
  }

  private getRowData(rowData: any) {
    if (this.functionalities.pagination.isEnabled) {
      return this.functionalities.pagination.getRowData(rowData);
    } else {
      return (
        this.data &&
        this.data.includes(rowData) &&
        this.data.find((data: any) => {
          return data == rowData;
        })
      );
    }
  }

  public setRowExpansionState(rowData: any, expanded: boolean) {
    let foundRowData = this.getRowData(rowData);
    if (foundRowData) {
      let internalRowViewModel = this.getInternalRowViewModel(foundRowData);
      internalRowViewModel.expanded = expanded;
      if (internalRowViewModel.rowViewModel) {
        internalRowViewModel.rowViewModel.expanded = expanded; // DEPRECATED
      }
      this.initializeExpandedRows();
    } else {
      throw new Error(`Unable to expand/collapse row ${JSON.stringify(rowData)}. Row does not exist.`);
    }
  }

  private setRowSelection(rows: any[]) {
    this.gridOptions.api.deselectAll();
    this.gridOptions.api.forEachNode((node: any) => {
      if (rows.includes(node.data)) {
        node.setSelected(true);
      }
    });
  }

  private setColumnWidth(columnId: string, width: number) {
    this.gridOptions.columnApi.setColumnWidth(columnId, width);
  }

  private moveColumn(columnId: string, toIndex: number) {
    this.gridOptions.columnApi.moveColumn(columnId, toIndex);
  }

  public setColumnSort(columnId: string, sort: any) {
    let sortString = this.convertSortEmumToSortString(sort);
    let sortModel: any[] = [];
    if (sortString) {
      sortModel = [{ colId: columnId, sort: sortString }];
    }
    this.gridOptions.api.setSortModel(sortModel);
  }

  /** Handle expanding row ourselves instead of ag-grid to allow renderer with accessibility data. */
  private onCellClicked(params: any) {
    if (params.colDef.expander) {
      // for some reason, without the timeout, expanding rows prevents ag-grid's default behavior for on-cell-click and on-row-click isn't fired.
      window.setTimeout(this.setRowExpansionState.bind(this), 0, params.data, !params.node.expanded);
    }
  }

  private getHeaderTemplate(params: any) {
    let headerAlignmentClass = leftAlignmentClass;
    switch (params.column.colDef.horizontalAlignment) {
      case "right":
        headerAlignmentClass = rightAlignmentClass;
        break;
      case "center":
        headerAlignmentClass = centerAlignmentClass;
        break;
      default:
        headerAlignmentClass = leftAlignmentClass;
    }
    let object = angular.element(
      `<button ng-click="sdTableController.sortColumn($event)" class="sd-table-header-button c-caption-2 ${
        this.gridOptions.enableSorting ? "" : "sd-table-header-no-sort"
      }" aria-label="{{sdTableController.getAriaHeaderLabel('` +
        params.column.colDef.id +
        `')}}">` +
        `<div id="agResizeBar" class="ag-header-cell-resize"></div>` +
        `<span id="agMenu" class="ag-header-icon ag-header-cell-menu-button"></span>` +
        `<div id="agHeaderCellLabel" class="ag-header-cell-label ${headerAlignmentClass}">` +
        `<span id="agSortAsc" class="c-caption-2 sd-glyph-up sd-table-header-button"></span>` +
        `<span id="agSortDesc" class="c-caption-2 sd-glyph-down sd-table-header-button"></span>` +
        `<span id="agNoSort" class="ag-header-icon ag-sort-none-icon"></span>` +
        `<span id="agFilter" class="ag-header-icon ag-filter-icon"></span>` +
        `<span id="agText" class="ag-header-cell-text"></span>` +
        `</div>` +
        `</button>`
    );
    object.on("keydown", (event) => {
      if (event.keyCode == this.uiControlConstants.keyCode.tabKeyCode) {
        let nextCol: any;
        if (event.shiftKey) {
          nextCol = this.gridOptions.columnApi.getDisplayedColBefore(params.column);
          if (nextCol && nextCol.colId == expanderColumnId) {
            nextCol = undefined;
          }
        } else {
          nextCol = this.gridOptions.columnApi.getDisplayedColAfter(params.column);
        }
        if (nextCol && nextCol.$sdElement) {
          this.gridOptions.api.ensureColumnVisible(nextCol.colId);
          nextCol.$sdElement.focus();
          event.preventDefault();
        } else if (event.shiftKey) {
          this.$element.children(".sd-table-tabbable-div").focus();
          event.preventDefault();
        } else {
          let orderedColumns = this.gridOptions.columnApi.getAllGridColumns();
          if (orderedColumns.length > 0) {
            let firstColumn = orderedColumns[0];
            this.gridOptions.api.ensureColumnVisible(firstColumn.colId);
            this.gridOptions.api.setFocusedCell(0, firstColumn.colId);
            event.preventDefault();
          }
        }
      }
    });
    params.column.$sdElement = object;
    this.$compile(object)(this.$scope);
    return object[0];
  }

  public getAriaHeaderLabel(colId: string): string {
    let label: string;
    let sortingLabel: string;
    for (let i = 0; i < this.columnDefinitions.length; i++) {
      if (this.columnDefinitions[i].id == colId) {
        label = this.columnDefinitions[i].headerName;
        break;
      }
    }

    label += this.headerColumnLabel;
    if (!this.gridOptions.enableSorting) {
      return label + this.headerNonSortableLabel;
    } else {
      sortingLabel = this.headerSortableLabel;
    }

    let sortModel = this.gridOptions.api.getSortModel();
    for (let i = 0; i < sortModel.length; i++) {
      if (sortModel[i].colId == colId) {
        if (sortModel[i].sort == "asc") {
          sortingLabel = this.headerAcsendingLabel;
          break;
        } else if (sortModel[i].sort == "desc") {
          sortingLabel = this.headerDescendingLabel;
          break;
        }
        // leave blank if unsorted.
      }
    }

    label += sortingLabel;
    return label;
  }

  public sortColumn(event: any): void {
    if (!this.gridOptions.enableSorting) {
      return;
    }
    let sortModel = this.gridOptions.api.getSortModel();
    let attribute = event.target.attributes["colid"];
    if (attribute) {
      let colId = attribute.value;
      let found = false;
      for (let i = 0; i < sortModel.length; i++) {
        if (sortModel[i].colId == colId) {
          if (sortModel[i].sort == "asc") {
            sortModel[i].sort = "desc";
          } else {
            sortModel = [];
          }
          found = true;
        }
      }
      if (!found) {
        sortModel = [{ colId: colId, sort: "asc" }];
      }
      this.gridOptions.api.setSortModel(sortModel);
    }
  }

  public getRowHeight(params: any) {
    let rowIsNestedRow = params.node.flower;
    return rowIsNestedRow ? this.parsedExpandedRowHeight : this.parsedRowHeight;
  }

  private getInternalRowViewModel(object: any) {
    if (!object.$sd) {
      object.$sd = {};
    }
    return object.$sd;
  }

  private getInternalViewModel(): SdTableMethods {
    let object = this.data || this.functionalities.pagination.dataSource;
    if (!object.$sd) {
      object.$sd = {};
    }
    return object.$sd;
  }

  public onRowGroupOpened(params: any) {
    this.agGridRowIsExpanding = false;
    if (this.requiresModelUpdate) {
      this.requiresModelUpdate = false;
      this.gridOptions.api.onRowHeightChanged();
    }
    let internalRowViewModel = this.getInternalRowViewModel(params.node.data);
    if (!internalRowViewModel.rowViewModel) {
      internalRowViewModel.rowViewModel = {};
    }
    internalRowViewModel.rowViewModel.expanded = params.node.expanded;
    internalRowViewModel.expanded = params.node.expanded;
    if (params.node.expanded) {
      this.notifyWatcherOfRowLifecycleEvent(params.node.data, this.tableConstants.rowLifecycleEventType.EXPANDED);
      this.invokeTelemetry(
        "Row expanded",
        `Row ${params.node.rowIndex} expanded`,
        this.telemetryConstants.pageActionType.click
      );
    } else if (!params.node.expanded && params.node._rowRenderScope && this.keepExpandedScopeOnCollapse != "true") {
      this.notifyWatcherOfRowLifecycleEvent(params.node.data, this.tableConstants.rowLifecycleEventType.COLLAPSED);
      params.node._rowRenderScope.$destroy();
      this.aliveRowScopes--;
      delete params.node._rowRenderScope;
      delete params.node._rowElement;
    }
  }

  public onVirtualRowRemoved(params: any) {
    let node = params.node;
    if (node._cellScopes) {
      for (let j = 0; j < node._cellScopes.length; j++) {
        node._cellScopes[j].$destroy();
        this.aliveCellScopes--;
      }

      delete node._cellScopes;
    }
    if (node._rowRenderScope && this.keepExpandedScopeOnScroll != "true") {
      this.notifyWatcherOfRowLifecycleEvent(node.data, this.tableConstants.rowLifecycleEventType.VIRTUALIZED_OUT);
      node._rowRenderScope.$destroy();
      this.aliveRowScopes--;
      delete node._rowRenderScope;
      delete node._rowElement;
    }
  }

  private notifyWatcherOfTableLifecycleEvent(changeType: any) {
    if (this.data) {
      let internalViewModel = this.getInternalViewModel();
      if (internalViewModel.viewModel && internalViewModel.viewModel.onTableLifecycleEvent) {
        internalViewModel.viewModel.onTableLifecycleEvent(changeType, this.data);
      }
    }
  }

  private notifyWatcherOfRowLifecycleEvent(data: any, changeType: any) {
    let internalRowViewModel = this.getInternalRowViewModel(data);
    if (internalRowViewModel.rowViewModel && internalRowViewModel.rowViewModel.onRowLifecycleEvent) {
      // DEPRECATED
      internalRowViewModel.rowViewModel.onRowLifecycleEvent(changeType, data);
    }
    if (angular.isFunction(internalRowViewModel.onRowLifecycleEvent)) {
      internalRowViewModel.onRowLifecycleEvent(changeType, data);
    }
    let internalViewModel = this.getInternalViewModel();
    if (angular.isFunction(internalViewModel.onRowLifecycleEvent)) {
      internalViewModel.onRowLifecycleEvent(changeType, data);
    }
  }

  private getCurrentDataLength() {
    return this.functionalities.pagination.isEnabled
      ? this.functionalities.pagination.getCurrentDataLength()
      : this.data.length;
  }

  public getFullWidthCellRenderer(compile: any) {
    return (params: any) => {
      // Embeded rows have different left pinned areas that we don't care about.
      if (params.eParentOfValue.parentElement.className != "ag-body-container") {
        // need to return something so ag-grid doesn't blow up.
        return document.createElement("div");
      }
      if (params.node.parent._rowElement) {
        // This should only be true for keep row scope.
        return params.node.parent._rowElement;
      }
      let rowIndex = params.node.parent.childIndex;
      let expandableRowAriaLabel =
        this.expandableRowButtonLabelPrefix + (rowIndex + 1) + this.expandableRowLabelPostfix;
      let template = `<div tabindex="0" ng-transclude="expandable" aria-label="` + expandableRowAriaLabel + `"></div>`;
      let eTemp = angular.element(template);
      let childScope = <any>this.compileScope.$parent.$new();
      this.aliveRowScopes++;
      params.node.parent._rowRenderScope = childScope;

      childScope.element = params.node.data;
      let setRowHeight = (height: number) => {
        if (!angular.isNumber(height)) {
          throw new Error("row height provided is not a number: " + height);
        }
        if (params.node.rowHeight != height) {
          params.node.setRowHeight(height);
          // if its expanding, mark model as dirty and reassess row height later. Else, do it now.
          if (this.agGridRowIsExpanding) {
            this.requiresModelUpdate = true;
          } else {
            this.gridOptions.api.onRowHeightChanged();
          }
        }
      };
      childScope.setRowHeight = setRowHeight; // DEPRECATED
      this.getInternalRowViewModel(childScope.element).setRowHeight = setRowHeight;
      let eGui: any = eTemp;
      compile(eGui, this.compileTransclusionFn)(childScope);
      params.node.parent._rowElement = eGui[0];
      this.$timeout(() => {
        // needs to happen after ag-grid has inserted element into table.
        this.scrollElementWithTable(params.eParentOfValue);
      });
      // handle up/down arrows within expanded row. Should focus on lastfocused column or first column.
      eGui[0].onkeydown = (event: any) => {
        if (!this.lastColumnFocused && this.columnDefinitions.length > 0) {
          this.lastColumnFocused = this.columnDefinitions[0].id;
        }
        // params.node.parent.childIndex is the proper index of a row before expanding, but we need params.rowIndex to tell ag-grid where to focus.
        let rowIndexToSelect: number;
        let prevRowIndex: number;
        if (event.keyCode == this.uiControlConstants.keyCode.upArrowKeyCode) {
          rowIndexToSelect = params.rowIndex - 1;
          prevRowIndex = params.rowIndex + 1;
          this.gridOptions.api.setFocusedCell(rowIndexToSelect, this.lastColumnFocused);
        } else if (
          event.keyCode == this.uiControlConstants.keyCode.downArrowKeyCode &&
          rowIndex + 1 < this.getCurrentDataLength()
        ) {
          rowIndexToSelect = params.rowIndex + 1;
          prevRowIndex = params.rowIndex - 1;
          this.gridOptions.api.setFocusedCell(params.rowIndex + 1, this.lastColumnFocused);
        }
        if (rowIndexToSelect != undefined && this.rowSelectMode) {
          let node = this.gridOptions.api.getRenderedNodes().find((node: any) => {
            return node.rowIndex == rowIndexToSelect;
          });
          if (!this.shiftKeyDown) {
            this.gridOptions.api.deselectAll();
          } else if (node.selected) {
            // deselect prev non expanded row. We are now reducing our selection.
            let prevNode = this.gridOptions.api.getRenderedNodes().find((node: any) => {
              return node.rowIndex == prevRowIndex;
            });
            if (prevNode) {
              prevNode.setSelected(false);
            }
          }

          node.setSelected(true);
        }
      };
      return eGui[0];
    };
  }

  public getCustomCellRenderer(
    compile: ng.ICompileService,
    transcludeFn: ng.ITranscludeFunction,
    colScope: any,
    horizontalAlignmentCssClass: string,
    verticalAlignmentCssClass: string
  ) {
    let templateLinkingFunction = compile(
      `<div ng-cloak class="${cellContainerClass}"><div class="${cellDefaultClass} ${horizontalAlignmentCssClass} ${verticalAlignmentCssClass}"><div ng-transclude="cellTemplate" ng-cloak></div></div></div>`,
      transcludeFn
    );
    return (params: any) => {
      return this.renderAngularCell(templateLinkingFunction, params, colScope);
    };
  }

  public getAutoUpdatingCellRenderer(
    compile: any,
    transcludeFn: any,
    colScope: any,
    horizontalAlignmentCssClass: string,
    verticalAlignmentCssClass: string
  ) {
    return (params: any) => {
      let templateLinkingFunction = compile(
        `<div tabindex="0" class="${cellContainerClass}"><div class="${cellDefaultClass} ${horizontalAlignmentCssClass} ${verticalAlignmentCssClass}"><span ng-if="row">{{row.${params.colDef.id}}}</span><span ng-hide="row">${params.value}</span></div></div>`,
        transcludeFn
      );
      return this.renderAngularCell(templateLinkingFunction, params, colScope);
    };
  }

  public renderAngularCell(templateLinkingFunction: ng.ITemplateLinkingFunction, params: any, colScope: any) {
    let childScope = colScope.$parent.$new();
    this.aliveCellScopes++;
    childScope.element = params.value;
    childScope.row = params.data;
    childScope.field = params.colDef.field;
    let clonedElement: JQuery;
    templateLinkingFunction(childScope, (clone) => {
      clonedElement = clone;
    });
    if (params.node._cellScopes) {
      params.node._cellScopes.push(childScope);
    } else {
      params.node._cellScopes = [childScope];
    }
    if (!this.fastRender) {
      if (!this.$rootScope.$$phase) {
        childScope.$apply();
      }
    }
    return clonedElement[0];
  }

  private initializeTableState() {
    if (this.data || this.functionalities.pagination.isEnabled) {
      this.initializeExpandedRows();
      this.initializeTableSort();
      this.initializeColumnWidths();
      this.initializeColumnOrder();
      this.initializeSelectedRows();
      this.agBodyViewport = this.$element.find(".ag-body-viewport")[0];
      this.agBodyContainer = this.$element.find(".ag-body-container")[0];
    }
  }

  private initializeSelectedRows() {
    let internalViewModel = this.getInternalViewModel();
    if (internalViewModel.rowSelection) {
      this.setRowSelection(internalViewModel.rowSelection);
    }
  }

  private initializeColumnOrder() {
    let internalViewModel = this.getInternalViewModel();

    if (internalViewModel.columnOrder) {
      this.gridOptions.columnApi.moveColumns(internalViewModel.columnOrder, 0);
    }
  }

  private initializeColumnWidths() {
    let internalViewModel = this.getInternalViewModel();
    if (internalViewModel.columnSizes) {
      Object.keys(internalViewModel.columnSizes).forEach((columnId) => {
        this.setColumnWidth(columnId, internalViewModel.columnSizes[columnId].width);
      });
    }
  }

  private initializeTableSort() {
    let internalViewModel = this.getInternalViewModel();
    if (internalViewModel.sortModel && internalViewModel.sortModel.length > 0) {
      this.setColumnSort(internalViewModel.sortModel[0].columnId, internalViewModel.sortModel[0].sort);
    }
  }

  public initializeExpandedRows() {
    let expansionChangedRows: any = [];
    this.gridOptions.api.forEachLeafNode((rowNode: any) => {
      let internalRowViewModel = this.getInternalRowViewModel(rowNode.data);
      if (rowNode.canFlower) {
        let requestedExpansionState: boolean;
        if (internalRowViewModel.rowViewModel && internalRowViewModel.rowViewModel.expanded != undefined) {
          requestedExpansionState = internalRowViewModel.rowViewModel.expanded; // DEPRECATED
        } else if (internalRowViewModel.expanded === undefined && this.expanderColumnHidden) {
          requestedExpansionState = true;
        } else {
          requestedExpansionState = internalRowViewModel.expanded == true;
        }
        if (requestedExpansionState != rowNode.expanded) {
          expansionChangedRows.push({ row: rowNode, shouldBeExpanded: requestedExpansionState });
        }
      }
    });
    if (expansionChangedRows.length > 0) {
      this.agGridRowIsExpanding = true;
      this.$timeout(() => {
        expansionChangedRows.forEach((rowContainer: any) => {
          rowContainer.row.setExpanded(rowContainer.shouldBeExpanded);
        });
      });
    }
  }

  private notifyRowsOfLifeCycleEvent(data: any[], event: any) {
    if (data) {
      for (let i = 0; i < data.length; i++) {
        this.notifyWatcherOfRowLifecycleEvent(data[i], event);
      }
    }
  }

  private initializeGridOptions() {
    let onReadyCallbacks: (() => void)[] = [];

    this.gridOptions = <SdkGridOptions & any>{
      rowData: this.data,
      suppressAutoSize: false,
      autoSizePadding: 10,
      headerHeight: this.headerHeight ? parseInt(<any>this.headerHeight) : undefined,
      enableColResize: this.resizableColumns,
      rowSelection: this.rowSelectMode ? this.rowSelectMode.toLowerCase() : false,
      angularCompileHeaders: true,
      defaultColDef: {
        suppressMovable: !this.arrangeableColumns,
        headerCellTemplate: this.getHeaderTemplate.bind(this)
      },
      suppressRowClickSelection: true,
      enableSorting: this.sortable ? (<any>this.sortable).toLowerCase() == "true" : false,
      isFullWidthCell: function (rowNode: any) {
        let rowIsNestedRow = rowNode.flower;
        return rowIsNestedRow;
      },
      fullWidthCellRenderer: this.getFullWidthCellRenderer(this.$compile),
      headerCellRenderer: (params: any) => {
        let wrapper = angular.element(`<span class="c-caption-2"></span>`)[0];
        wrapper.innerText = params.colDef.headerName;
        return wrapper;
      },
      getRowHeight: this.getRowHeight.bind(this),
      doesDataFlower: () => {
        return this.isExpandable;
      },
      icons: {
        sortAscending: '<button class="c-caption-2 sd-glyph-up sd-table-header-button"></button>',
        sortDescending: '<button class="c-caption-2 sd-glyph-down sd-table-header-button"></button>',
        groupExpanded: '<a class="sd-glyph-chevron-up"/>',
        groupContracted: '<a class="sd-glyph-chevron-down"/>',
        columnMovePin: '<button class="sd-glyph-pinned sd-table-header-button"></button>',
        columnMoveMove: '<button class="sd-glyph-move sd-table-header-button"></button>',
        columnMoveHide: '<button class="sd-glyph-minus sd-table-header-button"></button>',
        columnMoveLeft: '<button class="sd-glyph-arrow-left sd-table-header-button"></button>'
      },
      suppressDragLeaveHidesColumns: true, // prevent columns from being hidden by dragging them off the table.
      navigateToNextCell: this.overrideCellUpDownArrowBehavior.bind(this),
      tabToNextCell: this.overrideTabBehavior.bind(this),
      onRowClicked: this.onRowClicked.bind(this),
      onRowDataChanged: () => {
        let dataLength = this.data ? this.data.length : 0;
        this.invokeTelemetry(
          "Data Loaded Or Replaced",
          `Rows: ${dataLength} Columns: ${this.columnDefinitions.length}`,
          this.telemetryConstants.pageActionType.auto
        );
      },
      onVirtualRowRemoved: this.onVirtualRowRemoved.bind(this),
      onRowGroupOpened: this.onRowGroupOpened.bind(this),
      onCellClicked: this.onCellClicked.bind(this),
      onColumnMoved: this.onColumnMoved.bind(this),
      onSelectionChanged: this.onRowSelectionChanged.bind(this),
      onSortChanged: this.onColumnSorted.bind(this),
      onColumnResized: this.onColumnResized.bind(this),
      isExternalFilterPresent: this.isExternalFilterPresent.bind(this),
      doesExternalFilterPass: this.dataFilterPredicate.bind(this),
      onBodyScroll: this.onBodyScroll.bind(this),
      columnDefs: this.columnDefinitions,
      embedFullWidthRows: true,
      onGridReady: () => {
        this.isReadyToProcessDataUpdates = true;
        if (this.pendingTableData) {
          this.processRowData(this.pendingTableData);
        } else {
          this.initializeTableState();
        }
        onReadyCallbacks.forEach((x) => x(), this);
      },
      onGridSizeChanged: () => {
        // this event is triggered on load, column resize finish - ideal place to refit the columns if needed.
        this.autoResizeColumns();
      }
    };

    this.gridOptions._sd_whenGridReady = (callback: () => void) => {
      onReadyCallbacks.push(callback);
    };

    (<TableFunctionality[]>Object.values(this.functionalities))
      .filter((functionality) => functionality.isEnabled && functionality.extendGridOptions)
      .forEach((functionality) => functionality.extendGridOptions(this.gridOptions));

    if (this.autoHeight && (<any>this.autoHeight).toLowerCase() === "true") {
      this.gridOptions.domLayout = "autoHeight";
    }

    if (this.deltaRowConfiguration) {
      this.gridOptions.deltaRowDataMode = true;
      if (!this.deltaRowConfiguration.getRowNodeId) {
        throw new Error("getRowNodeId must be provided to use delta row data mode.");
      }

      this.gridOptions.getRowNodeId = this.deltaRowConfiguration.getRowNodeId;
    }
  }

  private autoResizeColumns() {
    if (this.autoFitColumns) {
      this.$timeout(() => {
        this.gridOptions.api.sizeColumnsToFit();
      }, 0);
    }
  }
  private initializeLocalControllerState(isExpandable: boolean) {
    if (isExpandable && this.columnDefinitions.length > 0) {
      this.addExpanderColumn();
      this.isExpandable = true;
    }

    // TODO: Might want to later add automatic column widths here.

    this.allowExportAsCsv = this.csvExport ? (<any>this.csvExport).toLowerCase() == "true" : false;
    this.$scope.$on("$destroy", () => {
      this.notifyRowsOfLifeCycleEvent(this.data, this.tableConstants.rowLifecycleEventType.DESTROYED);
      this.notifyWatcherOfTableLifecycleEvent(this.tableConstants.tableLifecycleEventType.DESTROYED);
    });

    this.headerColumnLabel = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_HEADER_COLUMN_"
    );
    this.headerNonSortableLabel = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_HEADER_NON_SORTABLE_"
    );
    this.headerSortableLabel = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_HEADER_SORTABLE_"
    );
    this.headerAcsendingLabel = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_HEADER_ACSENDING_"
    );
    this.headerDescendingLabel = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_DESCENDING_"
    );
    this.expandableRowButtonLabelPrefix = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_EXPANDABLE_ROW_BUTTON_PREFIX_"
    );
    this.expandableRowButtonLabelPostfix = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_EXPANDABLE_ROW_BUTTON_POSTFIX_"
    );
    this.expandableRowButtonExpandedLabel = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_EXPANDABLE_ROW_BUTTON_EXPANDED_"
    );
    this.expandableRowButtonContractedLabel = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_EXPANDABLE_ROW_BUTTON_CONTRACTED_"
    );
    this.expandableRowLabelPrefix = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_EXPANDABLE_ROW_PREFIX_"
    );
    this.expandableRowLabelPostfix = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_EXPANDABLE_ROW_POSTFIX_"
    );
    this.cellRowLabelPrefix = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_CELL_ROW_PREFIX_"
    );
    this.cellColumnLabelPrefix = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_CELL_COLUMN_PREFIX_"
    );
    this.cellHeaderValueLabelSpacer = this.sdLocalizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_HEADER_VALUE_SPACER_"
    );

    this.documentInteractionService.subscribeToKeyDown(this.keyEvent.bind(this));
    this.documentInteractionService.subscribeToKeyUp(this.keyEvent.bind(this));

    this.parsedRowHeight = this.rowHeight ? parseInt(this.rowHeight) : defaultRowHeight;
    if (isNaN(this.parsedRowHeight)) {
      throw new Error(`row-height is expected to be a number, got: ${this.rowHeight}`);
    }
    this.parsedExpandedRowHeight = this.expandedRowHeight ? parseInt(this.expandedRowHeight) : defaultExpandedRowHeight;
    if (isNaN(this.parsedExpandedRowHeight)) {
      throw new Error(`expanded-row-height is expected to be a number, got: ${this.expandedRowHeight}`);
    }
  }

  private initializeTableData() {
    this.addViewModelToData();
    this.oldData = this.data;
    this.$scope.$watch(
      () => {
        return this.data;
      },
      (newValue, oldValue) => {
        if (newValue != this.gridOptions.rowData) {
          this.processRowData(newValue);
        }
      }
    );
  }

  private processRowData(data: any) {
    if (this.isReadyToProcessDataUpdates) {
      this.notifyRowsOfLifeCycleEvent(this.oldData, this.tableConstants.rowLifecycleEventType.DATA_CHANGED);
      this.oldData = this.data;
      let newData = data ? data : [];
      this.gridOptions.api.setRowData(newData);
      this.gridOptions.rowData = newData;
      this.initializeTableState();
      this.addViewModelToData();
    } else {
      this.pendingTableData = data;
    }
  }

  public initialize(isExpandable: boolean) {
    this.initializeGridOptions();
    this.initializeLocalControllerState(isExpandable);
    this.initializeTableData();

    this.readyForAgGridToStartRendering = true;
    this.notifyWatcherOfTableLifecycleEvent(this.tableConstants.tableLifecycleEventType.CREATED);
  }

  public tabbableDivKeydown(event: JQueryEventObject) {
    let divElement = angular.element(event.target);
    // all table key events are captured by this. Make sure it is indeed the tabbable div obj.
    let isFocused =
      divElement.is(":focus") && divElement.attr("id") && divElement.attr("id").startsWith("sdtable-tabbable-div");
    if (isFocused && event.keyCode == this.uiControlConstants.keyCode.tabKeyCode && !event.shiftKey) {
      let orderedColumns = this.gridOptions.columnApi.getAllGridColumns();
      if (orderedColumns.length > 0) {
        let firstColumn = orderedColumns[0];
        if (firstColumn.colId == expanderColumnId) {
          if (orderedColumns.length > 1) {
            firstColumn = orderedColumns[1];
          } else {
            firstColumn = undefined;
          }
        }
        if (firstColumn && firstColumn.$sdElement) {
          this.gridOptions.api.ensureColumnVisible(firstColumn.colId);
          firstColumn.$sdElement.focus();
          event.preventDefault();
        }
      }
    }
  }

  public onSearchChange(searchText: string) {
    if (!this.onSearch) {
      this.gridOptions.api.setQuickFilter(searchText);
    } else {
      this.searchText = searchText;
      this.gridOptions.api.onFilterChanged();
    }
  }

  // If expandable, add + icon to first column for expanding.
  private addExpanderColumn() {
    this.columnDefinitions.unshift({
      headerName: "",
      field: expanderColumnId,
      id: expanderColumnId,
      width: 50,
      suppressMovable: true,
      suppressSorting: true,
      suppressResize: true,
      suppressFilter: true,
      expander: true,
      hide: this.expanderColumnHidden,
      cellRenderer: (params: any) => {
        // keyboard nav, pressing enter acts as click to expand row.
        params.eGridCell.onkeypress = (event: any) => {
          if (event.keyCode == this.uiControlConstants.keyCode.enterKeyCode) {
            params.eGridCell.click();
          }
        };
        params.eGridCell.setAttribute(
          "aria-label",
          this.expandableRowButtonLabelPrefix +
            (params.rowIndex + 1) +
            this.expandableRowButtonLabelPostfix +
            (params.node.expanded ? this.expandableRowButtonExpandedLabel : this.expandableRowButtonContractedLabel)
        );
        let element: any;
        let expanderClass = "sd-table-expander";
        if (params.node.expanded) {
          element = angular.element(
            `<div class="${cellContainerClass} ${expanderClass}"><div class="${cellDefaultClass} ${verticalCenterAlignmentClass}">${this.gridOptions.icons.groupExpanded}</div></div>`
          )[0];
        } else {
          element = angular.element(
            `<div class="${cellContainerClass} ${expanderClass}"><div class="${cellDefaultClass} ${verticalCenterAlignmentClass}">${this.gridOptions.icons.groupContracted}</div></div`
          )[0];
        }

        this.$compile(element)(this.$scope);
        return element;
      }
    });
  }

  public addColumnData(columnData: ColumnController, customRender: any, transcludeFn: ng.ITranscludeFunction) {
    let columnDef: any = {
      headerName: columnData.label,
      field: columnData.field,
      width: columnData.width ? parseInt(<any>columnData.width) : 100,
      hide: columnData.hidden ? columnData.hidden : false,
      id: columnData.id ? columnData.id : columnData.field,
      horizontalAlignment: columnData.horizontalAlignment,
      verticalAlignment: columnData.verticalAlignment,
      exportFormatter: columnData.exportFormatter,
      suppressSizeToFit: columnData.suppressAutoFit || false
    };
    let cellHorizontalAlignmentClass = leftAlignmentClass;
    switch (columnData.horizontalAlignment) {
      case "right":
        cellHorizontalAlignmentClass = rightAlignmentClass;
        break;
      case "center":
        cellHorizontalAlignmentClass = centerAlignmentClass;
        break;
      default:
        cellHorizontalAlignmentClass = leftAlignmentClass;
    }
    let cellVerticalAlignmentClass = verticalCenterAlignmentClass;
    switch (columnData.verticalAlignment) {
      case "top":
        cellVerticalAlignmentClass = verticalTopAlignmentClass;
        break;
      case "bottom":
        cellVerticalAlignmentClass = verticalBottomAlignmentClass;
        break;
      default:
        cellVerticalAlignmentClass = verticalCenterAlignmentClass;
    }
    if (customRender == this.tableConstants.cellRenderType.CUSTOM) {
      columnDef.cellRenderer = this.getCustomCellRenderer(
        this.$compile,
        transcludeFn,
        columnData.$scope,
        cellHorizontalAlignmentClass,
        cellVerticalAlignmentClass
      );
      columnDef.cellRendererType = customRender;
    } else if (customRender == this.tableConstants.cellRenderType.AUTO) {
      columnDef.cellRenderer = this.getAutoUpdatingCellRenderer(
        this.$compile,
        transcludeFn,
        columnData.$scope,
        cellHorizontalAlignmentClass,
        cellVerticalAlignmentClass
      );
      columnDef.cellRendererType = customRender;
    } else {
      columnDef.cellRenderer = (params: any) => {
        let ariaLabel =
          this.cellRowLabelPrefix +
          (params.rowIndex + 1) +
          this.cellColumnLabelPrefix +
          columnDef.headerName +
          this.cellHeaderValueLabelSpacer +
          params.value;
        params.eGridCell.setAttribute("aria-label", ariaLabel);
        params.eGridCell.setAttribute("aria-readonly", true);
        return `<div tabindex="0" class="${cellContainerClass}"><span class="${cellDefaultClass} ${cellHorizontalAlignmentClass} ${cellVerticalAlignmentClass}">${params.value}</span></div>`;
      };
      columnDef.cellRendererType = this.tableConstants.cellRenderType.DEFAULT;
    }

    if (columnData.isFilterable && (this.data || this.functionalities.pagination.isEnabled)) {
      this.filterableColumns.push({
        columnBinding: columnData.field,
        columnLabel: columnData.label,
        getDataSource: () => {
          return this.data || this.functionalities.pagination.dataSource;
        },
        filteredToValues: columnData.defaultFilterValues || [],
        filterer: columnData.filterer || (this.functionalities.pagination.isEnabled ? undefined : primitiveFilterer)
      });
    }
    if (columnData.sortComparator) {
      columnDef.comparator = (o1: any, o2: any) => columnData.sortComparator({ o1: o1, o2: o2 });
    }

    this.columnDefinitions.push(columnDef);
    this.columnFieldDefinitions.push(columnData.field);
  }

  public updateColumnVisibility(columnId: string, isVisible: boolean) {
    if (this.isReadyToProcessDataUpdates) {
      this.gridOptions.columnApi.setColumnVisible(columnId, isVisible);
    }
  }

  // documentation here : https://www.ag-grid.com/javascript-grid-export/
  public exportAsCsv(exportOptions?: { fileName: string; excludeColumnKeys: string[] }) {
    let fileName = exportOptions ? exportOptions.fileName : undefined;
    let formatter = (param: any) => {
      if (param.column.colDef.exportFormatter) {
        return param.column.colDef.exportFormatter(param.value);
      } else {
        return param.value;
      }
    };

    let columnKeys: string[] = this.columnDefinitions
      .map((colDef) => colDef.id)
      .filter((colId) => colId != expanderColumnId);

    if (exportOptions.excludeColumnKeys) {
      columnKeys = columnKeys.filter((colId: string) => exportOptions.excludeColumnKeys.indexOf(colId) === -1);
    }

    this.gridOptions.api.exportDataAsCsv({
      columnKeys: columnKeys,
      fileName: fileName,
      processCellCallback: formatter
    });
  }

  private overrideTabBehavior(params: any) {
    if (params.backwards && !params.nextCellDef) {
      let orderedColumns = this.gridOptions.columnApi.getAllGridColumns();
      let lastColumn = orderedColumns[orderedColumns.length - 1];
      this.gridOptions.api.ensureColumnVisible(lastColumn.colId);
      // without this, focus will be set on second to last column since there is no way to prevent default for this event.
      let setFocusFunc = () => {
        lastColumn.$sdElement.focus();
      };
      window.setTimeout(setFocusFunc, 0);
      return params.prevCellDef;
    }

    let getTabbableElements: (cellDef: any) => JQuery = (cellDef: any) => {
      let rowElement = this.$element.find(`.ag-body-container > div[row="${cellDef.rowIndex}"]`).first();
      let cell = rowElement.find(`div[colid="${cellDef.column.colId}"]`).first();
      return cell.find("a, button, [tabindex=0]");
    };

    if (
      params.backwards &&
      params.nextCellDef &&
      params.nextCellDef.column &&
      params.nextCellDef.column.colDef &&
      params.nextCellDef.column.colDef.cellRendererType === this.tableConstants.cellRenderType.CUSTOM
    ) {
      let tabbableElements = getTabbableElements(params.nextCellDef);
      if (tabbableElements.length) {
        return;
      }
    }

    if (
      params.previousCellDef &&
      params.previousCellDef.column &&
      params.previousCellDef.column.colDef &&
      params.previousCellDef.column.colDef.cellRendererType === this.tableConstants.cellRenderType.CUSTOM
    ) {
      let tabbableElements = getTabbableElements(params.previousCellDef);
      if (tabbableElements.length) {
        if (params.backwards && tabbableElements[0] === document.activeElement) {
          return params.previousCellDef;
        }
        return;
      }
    }
    return params.nextCellDef;
  }

  private overrideCellUpDownArrowBehavior(params: any) {
    if (
      params.nextCellDef &&
      (params.key == this.uiControlConstants.keyCode.downArrowKeyCode ||
        params.key == this.uiControlConstants.keyCode.upArrowKeyCode)
    ) {
      let renderedNodes = this.gridOptions.api.getRenderedNodes();
      let findRowIndex = params.nextCellDef.rowIndex - 1; // if going up, check parent of expanded row; going down, check if current row expanded first.
      let expandedRowNode = renderedNodes.find((node: any) => {
        return node.rowIndex == findRowIndex && node.expanded;
      });
      if (expandedRowNode) {
        this.gridOptions.api.ensureIndexVisible(expandedRowNode.rowIndex + 1);
        // wait for scroll to finish before focus
        setTimeout(() => {
          expandedRowNode._rowElement.focus();
        }, 50);
        this.lastColumnFocused = params.nextCellDef.column.colId;
        return undefined;
      } else if (this.rowSelectMode) {
        if (!this.shiftKeyDown) {
          this.gridOptions.api.deselectAll();
        }
        let goingToNode = renderedNodes.find((node: any) => {
          return node.rowIndex == params.nextCellDef.rowIndex;
        });
        // If the node we're going to is already selected, then we should unselect the prev node as the selection range is now decreasing.
        if (params.previousCellDef && goingToNode.selected) {
          let prevNode = renderedNodes.find((node: any) => {
            return node.rowIndex == params.previousCellDef.rowIndex;
          });
          if (prevNode) {
            prevNode.setSelected(false);
          }
        }
        goingToNode.setSelected(true);
      }
    }
    return params.nextCellDef;
  }

  private refreshFilters() {
    this.$scope.$broadcast(refreshFiltersEventName);
  }

  public dataFilterPredicate(rowItem: any): boolean {
    if (this.functionalities.pagination.isEnabled) {
      // Short circuit this predicate if we're paging.
      return;
    }

    return (
      (!this.hasColumnFilter() || this.columnFilterPredicate(rowItem)) &&
      (!this.hasSearchFilter() || this.searchFilterPredicate(rowItem))
    );
  }

  public columnFilterPredicate(rowItem: any): boolean {
    if (this.filterableColumns.length > 0) {
      for (let i = 0; i < this.filterableColumns.length; i++) {
        let column = this.filterableColumns[i];
        if (column.filteredToValues && column.filteredToValues.length > 0) {
          if (!column.filterer.matchExists(column, rowItem.data)) {
            return false;
          }
        }
      }
    }

    return true;
  }

  public isExternalFilterPresent(): boolean {
    return this.hasSearchFilter() || this.hasColumnFilter();
  }

  public hasSearchFilter(): boolean {
    return this.searchable && this.onSearch && this.searchText != undefined;
  }

  public hasColumnFilter(): boolean {
    if (this.filterableColumns.length > 0) {
      for (let i = 0; i < this.filterableColumns.length; i++) {
        let column = this.filterableColumns[i];
        if (column.filteredToValues && column.filteredToValues.length > 0) {
          return true;
        }
      }
    }

    return false;
  }

  public searchFilterPredicate(rowItem: any): boolean {
    let rowToBePersisted = false;
    if (this.onSearch) {
      rowToBePersisted = this.onSearch({
        searchText: this.searchText,
        rowItem: rowItem,
        columnFieldDef: this.columnFieldDefinitions
      });
    }

    return rowToBePersisted;
  }

  public invokeTelemetry(biId: string, biName: string, action?: string) {
    let tableElement = this.$element.find(".sd-table");
    let telemetryContent: any = {
      contentId: biId,
      slotNumber: 1,
      contentName: biName
    };
    for (const data in this.telemetryData) {
      if (this.telemetryData.hasOwnProperty(data)) {
        telemetryContent[data] = (this.telemetryData as any)[data];
      }
    }

    if (this.firstLoad) {
      this.$timeout(() => {
        this.telemetryService.pushPageActionElementEvent(
          tableElement,
          this.telemetryConstants.pageActionBehavior.Unknown,
          telemetryContent,
          action,
          undefined,
          this.pageMetaTags
        );
      }).then(() => {
        this.firstLoad = false;
      });
    } else {
      this.telemetryService.pushPageActionElementEvent(
        tableElement,
        this.telemetryConstants.pageActionBehavior.Unknown,
        telemetryContent,
        action,
        undefined,
        this.pageMetaTags
      );
    }
  }
}

export function sdTable(): ng.IDirective {
  return {
    scope: {},
    restrict: "E",
    templateUrl: "../ShellCommon/Controls/table/table.html",
    compile: function ($element: JQuery, $attrs: ng.IAttributes, compileTransclude: ng.ITranscludeFunction) {
      return {
        post: function (
          scope: ng.IScope,
          element: JQuery,
          attrs: ng.IAttributes,
          ctrl: TableController,
          transclude: any
        ) {
          ctrl.compileScope = scope;
          ctrl.compileTransclusionFn = compileTransclude;
          ctrl.initialize(transclude.isSlotFilled("expandable"));
        }
      };
    },
    controller: TableController,
    controllerAs: "sdTableController",
    transclude: {
      expandable: "?sdTableExpandable"
    },
    bindToController: <any>new TableBinding()
  };
}
